import { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Col } from 'react-bootstrap';
import FormContainer from '../Components/FormContainer';
import CheckoutSteps from '../Components/CheckoutSteps';
import { savePaymentMethod } from '../slices/cartSlice';
import { setCredentials } from '../slices/authSlice';
import { useGetUserProfileQuery } from '../slices/usersApiSlice';
import { Link } from 'react-router-dom';
import BackButton from "../Components/BackButton";
import {
  useGetPayPalClientIdQuery,
  useGetMyPendingPaymentsQuery,
} from '../slices/ordersApiSlice';
import { toast } from 'react-toastify';
import { loadPrefs } from '../utils/orderPreferences';

const PaymentScreen = () => {
  const [paymentMethod, setPaymentMethod] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cart = useSelector((state) => state.cart) || {};
  const { userInfo } = useSelector((state) => state.auth) || {};
  const {
    cartItems = [],
    shippingAddress = {},
    guestMode,
    fulfillmentMethod = 'ship',
  } = cart;

  const effectiveGuestMode = Boolean(guestMode) && !userInfo;

  // Per-user checkout preferences (only for authenticated users)
  const prefs = useMemo(
    () => (!effectiveGuestMode && userInfo?._id ? loadPrefs(userInfo._id) : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userInfo?._id, effectiveGuestMode]
  );

  const { data: profileData } = useGetUserProfileQuery(undefined, {
    skip: effectiveGuestMode || !userInfo,
  });

  const { data: pendingPaymentData } = useGetMyPendingPaymentsQuery(undefined, {
    skip: effectiveGuestMode || !userInfo,
  });

  const effectiveUserInfo = profileData || userInfo;
  const isTieredUser = Boolean(effectiveUserInfo?.tier?.id);
  const canUseEndOfMonthPayment = Boolean(
    effectiveUserInfo?.endOfMonthPaymentEnabled ?? pendingPaymentData?.enabled
  );

  // Pickup vs Shipping (explicit only)
  const isPickup = fulfillmentMethod === 'pickup';
  const isDelivery = fulfillmentMethod === 'ship';

  const { data: paypalConfig } = useGetPayPalClientIdQuery();
  const paypalEnabled = paypalConfig?.enabled ?? false;

  const needsAddress = !isPickup;

  const hasAddress =
    !!shippingAddress?.address &&
    !!shippingAddress?.city &&
    !!shippingAddress?.postalCode &&
    !!shippingAddress?.country;

  // ⬇️ Toggle behavior: click again to deselect
  const handlePaymentChange = (value) => {
    setPaymentMethod((current) => (current === value ? '' : value));
  };

  useEffect(() => {
    if (profileData) {
      dispatch(setCredentials(profileData));
    }
  }, [profileData, dispatch]);

  useEffect(() => {
    // Optional: no items → bounce to cart
    if (!cartItems.length) {
      navigate('/cart', { replace: true });
      return;
    }

    // Only enforce address if shipping
    if (needsAddress && !hasAddress) {
      navigate(effectiveGuestMode ? '/guest-shipping' : '/shipping', { replace: true });
    }
  }, [cartItems.length, needsAddress, hasAddress, effectiveGuestMode, navigate]);

  // Keep this one: if PayPal becomes disabled via env, unselect it
  useEffect(() => {
    if (!paypalEnabled && paymentMethod === 'PayPal') {
      if (isPickup) {
        setPaymentMethod('PickupPay');
      } else {
        setPaymentMethod('');
      }
    }
  }, [paypalEnabled, paymentMethod, isPickup]);

  useEffect(() => {
    if (!isTieredUser && paymentMethod === 'Payment at Delivery') {
      setPaymentMethod('');
    }
  }, [isTieredUser, paymentMethod]);

  useEffect(() => {
    if (!canUseEndOfMonthPayment && paymentMethod === 'End of Month Payment') {
      setPaymentMethod('');
    }
  }, [canUseEndOfMonthPayment, paymentMethod]);

  // Apply saved preference once after constraint data is fully loaded.
  // We wait for paypalConfig (the last async piece) before evaluating so we
  // don't set and then immediately clear the selection due to loading states.
  const prefApplied = useRef(false);
  useEffect(() => {
    if (prefApplied.current) return;
    if (!prefs.defaultPaymentMethod) return;
    if (paymentMethod) return;             // user already picked something
    if (paypalConfig === undefined) return; // wait for paypal config to load

    const preferred = prefs.defaultPaymentMethod;
    let valid = false;
    if (preferred === 'PayPal' && paypalEnabled) valid = true;
    if (preferred === 'PickupPay' && isPickup) valid = true;
    if (preferred === 'Payment at Delivery' && isTieredUser && isDelivery) valid = true;
    if (preferred === 'End of Month Payment' && canUseEndOfMonthPayment) valid = true;

    if (valid) setPaymentMethod(preferred);
    prefApplied.current = true;
  }, [
    prefs.defaultPaymentMethod,
    paypalConfig,
    paypalEnabled,
    isPickup,
    isDelivery,
    isTieredUser,
    canUseEndOfMonthPayment,
    paymentMethod,
  ]);

  const submitHandler = (e) => {
    e.preventDefault();

    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    dispatch(savePaymentMethod(paymentMethod));
    navigate(effectiveGuestMode ? '/guest-placeorder' : '/placeorder');
  };

  return (
    <>
      <BackButton />
      <FormContainer>
        <CheckoutSteps step1 step2 step3 />
        <h1>Payment Method</h1>
        <Form onSubmit={submitHandler}>
          <Form.Group>
            <Form.Label as="legend">Select Method</Form.Label>
            <Col>
              {/* PayPal / Card */}
              <Form.Check
                type="radio"
                className="my-2"
                label="PayPal or Credit Card"
                id="PayPal"
                name="paymentMethod"
                value="PayPal"
                checked={paymentMethod === 'PayPal'}
                onChange={() => handlePaymentChange('PayPal')}
                disabled={!paypalEnabled}
              />

              {!paypalEnabled && (
                <div className="text-muted small ms-1">
                  Online payment (PayPal / credit card) is temporarily unavailable.
                </div>
              )}

              {/* Pay on Pickup — disabled when shipping */}
              <Form.Check
                type="radio"
                className="my-2"
                label="Pay on Pickup"
                id="PickupPay"
                name="paymentMethod"
                value="PickupPay"
                checked={paymentMethod === 'PickupPay'}
                onChange={() => handlePaymentChange('PickupPay')}
                disabled={!isPickup}
              />

              {!isPickup && (
                <div className="text-muted small ms-1">
                  Pay in store isn't available for shipping orders. Please pay online or at delivery.
                </div>
              )}

              {isTieredUser && (
                <>
                  {/* Payment at Delivery — disabled when pickup */}
                  <Form.Check
                    type="radio"
                    className="my-2"
                    label="Payment at Delivery"
                    id="PaymentAtDelivery"
                    name="paymentMethod"
                    value="Payment at Delivery"
                    checked={paymentMethod === 'Payment at Delivery'}
                    onChange={() => handlePaymentChange('Payment at Delivery')}
                    disabled={!isDelivery}
                  />

                  {!isDelivery && (
                    <div className="text-muted small ms-1">
                      Payment at delivery is only available for shipped orders.
                    </div>
                  )}
                </>
              )}

              {canUseEndOfMonthPayment && (
                <>
                  <Form.Check
                    type="radio"
                    className="my-2"
                    label="End of Month Payment"
                    id="EndOfMonthPayment"
                    name="paymentMethod"
                    value="End of Month Payment"
                    checked={paymentMethod === 'End of Month Payment'}
                    onChange={() => handlePaymentChange('End of Month Payment')}
                  />
                </>
              )}
            </Col>
          </Form.Group>

          <Button
            type="submit"
            variant="primary"
            className="mt-3"
            disabled={!paymentMethod}
          >
            Continue
          </Button>

          <div className="mt-1">
            <small className="text-muted">
              See our <Link to="/shipping/policy">Shipping & Delivery Policy</Link> and{' '}
              <Link to="/returns/policy">Return & Refund Policy</Link>.
            </small>
          </div>
        </Form>
      </FormContainer>
    </>
  );
};

export default PaymentScreen;
